import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const CARD_SX = {
  backgroundColor: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: 3,
  p: 2.5,
  minHeight: 72,
} as const;

type KpiValue = { value: number; delta: { text: string; isUp: boolean } };

export type PointhomeContentStatusKpiData = {
  dau: KpiValue;
  participatedUser: KpiValue;
  transactionCount: KpiValue;
  participationRate: KpiValue;
  /** 획득 평균 포인트: 캐시 지급액 합계 ÷ 참여 완료 횟수 */
  avgCashPerTxn: KpiValue;
  /** 1인당 일 평균 획득 포인트: 캐시 지급액 합계 ÷ 참여 완료 사용자 수 ÷ 조회 일수 */
  avgCashPerUserPerDay: KpiValue;
  /** 일 한도 도달률: 최대 참여 도달 사용자 수 ÷ 참여 완료 사용자 수 (%) */
  maxReachedRate: KpiValue;
};

type Props = {
  data?: PointhomeContentStatusKpiData | null;
  contentName?: string;
};

function KpiCard({
  label,
  value,
  deltaText,
  isUp,
  tooltip,
}: {
  label: string;
  value: string;
  deltaText?: string;
  isUp?: boolean;
  tooltip?: string;
}) {
  return (
    <Box sx={CARD_SX}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary" fontWeight="bold">
          {label}
        </Typography>
        {tooltip && (
          <Tooltip title={tooltip} arrow placement="top">
            <IconButton size="small" sx={{ p: 0, color: '#64748b' }} aria-label={`${label} 설명`}>
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Typography variant="h6" fontWeight="bold" color="#374151">
        {value}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: '#64748b' }}>
        {deltaText ? (
          <>
            전월 동기간 대비{' '}
            <Box component="span" sx={{ color: isUp ? '#D32F2F' : '#1976D2', fontWeight: 600 }}>
              {deltaText}
            </Box>
          </>
        ) : (
          '—'
        )}
      </Typography>
    </Box>
  );
}

function CombinedKpiCard({
  label1,
  tooltip1,
  value1,
  delta1,
  label2,
  tooltip2,
  value2,
  delta2,
}: {
  label1: string;
  tooltip1?: string;
  value1: string;
  delta1?: { text: string; isUp: boolean };
  label2: string;
  tooltip2?: string;
  value2: string;
  delta2?: { text: string; isUp: boolean };
}) {
  return (
    <Box sx={CARD_SX}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary" fontWeight="bold">
          {label1}
        </Typography>
        {tooltip1 && (
          <Tooltip title={tooltip1} arrow placement="top">
            <IconButton size="small" sx={{ p: 0, color: '#64748b' }} aria-label={`${label1} 설명`}>
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        <Typography variant="body2" color="text.secondary" fontWeight="bold">
          /
        </Typography>
        <Typography variant="body2" color="text.secondary" fontWeight="bold">
          {label2}
        </Typography>
        {tooltip2 && (
          <Tooltip title={tooltip2} arrow placement="top">
            <IconButton size="small" sx={{ p: 0, color: '#64748b' }} aria-label={`${label2} 설명`}>
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Typography variant="h6" fontWeight="bold" color="#374151" sx={{ wordBreak: 'keep-all' }}>
        {value1} / {value2}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: '#64748b' }}>
        전월 동기간 대비{' '}
        {delta1 ? (
          <Box
            component="span"
            sx={{ color: delta1.isUp ? '#D32F2F' : '#1976D2', fontWeight: 600 }}
          >
            {delta1.text}
          </Box>
        ) : (
          '—'
        )}{' '}
        /{' '}
        {delta2 ? (
          <Box
            component="span"
            sx={{ color: delta2.isUp ? '#D32F2F' : '#1976D2', fontWeight: 600 }}
          >
            {delta2.text}
          </Box>
        ) : (
          '—'
        )}
      </Typography>
    </Box>
  );
}

export default function PointhomeContentStatusKpiCards({ data, contentName }: Props) {
  const name = contentName ?? '해당 콘텐츠';

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
          lg: 'repeat(6, 1fr)',
        },
        gap: 2,
        mt: 3,
      }}
    >
      <KpiCard
        label="활성 사용자 수"
        value={data ? `${data.dau.value.toLocaleString('ko-KR')}명` : '—'}
        deltaText={data?.dau.delta.text}
        isUp={data?.dau.delta.isUp}
        tooltip="조회된 기간 동안 포인트홈에 접속한 사용자 수 입니다. (중복 제외)"
      />
      <CombinedKpiCard
        label1="참여 완료 횟수"
        tooltip1={`조회된 기간 동안 ${name}에서 캐시를 적립 받은 횟수 입니다. (중복 포함)`}
        value1={data ? `${data.transactionCount.value.toLocaleString('ko-KR')}회` : '—'}
        delta1={data?.transactionCount.delta}
        label2="사용자 수"
        tooltip2={`조회된 기간 동안 ${name}에서 캐시를 적립 받은 고유 사용자 수 입니다.`}
        value2={data ? `${data.participatedUser.value.toLocaleString('ko-KR')}명` : '—'}
        delta2={data?.participatedUser.delta}
      />
      <KpiCard
        label="참여율"
        value={data ? `${data.participationRate.value}%` : '—'}
        deltaText={data?.participationRate.delta.text}
        isUp={data?.participationRate.delta.isUp}
        tooltip="참여 완료 사용자 수 ÷ 활성 사용자 수 입니다."
      />
      <KpiCard
        label="획득 평균 포인트"
        value={data ? `${data.avgCashPerTxn.value.toLocaleString('ko-KR')}P` : '—'}
        deltaText={data?.avgCashPerTxn.delta.text}
        isUp={data?.avgCashPerTxn.delta.isUp}
        tooltip="캐시 지급액 합계 ÷ 참여 완료 횟수 입니다."
      />
      <KpiCard
        label="1인당 일 평균 획득 포인트"
        value={data ? `${data.avgCashPerUserPerDay.value.toLocaleString('ko-KR')}P` : '—'}
        deltaText={data?.avgCashPerUserPerDay.delta.text}
        isUp={data?.avgCashPerUserPerDay.delta.isUp}
        tooltip="캐시 지급액 합계 ÷ 참여 완료 사용자 수 ÷ 조회 일수 입니다."
      />
      <KpiCard
        label="일 한도 도달률"
        value={data ? `${data.maxReachedRate.value}%` : '—'}
        deltaText={data?.maxReachedRate.delta.text}
        isUp={data?.maxReachedRate.delta.isUp}
        tooltip={`조회된 기간 동안 하루 중 ${name} 최대 참여 횟수까지 도달한 사용자 수 ÷ 참여 완료 사용자 수 입니다.`}
      />
    </Box>
  );
}
